import { useState, useEffect, useCallback } from 'react';
import { Modal, useModalClose } from '@/components/ui/Modal';
import type { ButtonHTMLAttributes } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Loader2, Package, Truck, Hash, Calendar, User,
  FileText, Plus, X, Award, CheckCircle2, AlertTriangle,
  ClipboardList, TrendingUp, TrendingDown,
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatINR, formatDate } from '@/utils/formatters';
import { loadVendorOptions, type VendorOption } from './vendorOptions';

interface QuotationEntry {
  id: string;
  vendorName: string;
  vendorContact?: string;
  vendorPhone?: string;
  rate: number;
  quantity?: number;
  totalAmount?: number;
  deliveryDays?: number;
  marginAmount?: number;
  marginPercent?: number;
  source: string;
  isAwarded: boolean;
  notes?: string;
  createdAt: string;
}

interface QuotationJob {
  id: string;
  number: string;
  type: string;
  description: string;
  quantity: number;
  unit?: string;
  status: string;
  assignedTo?: string;
  targetRate?: number;
  requiredByDate?: string;
  salesOrder: { id: string; number: string };
  quotations: QuotationEntry[];
  createdAt: string;
  updatedAt: string;
}

const STATUS_CONFIG: Record<string, { label: string; badge: string }> = {
  open: { label: 'Open', badge: 'bg-gray-100 text-gray-700' },
  in_progress: { label: 'In Progress', badge: 'bg-blue-50 text-blue-700' },
  quoted: { label: 'Quoted', badge: 'bg-yellow-50 text-yellow-700' },
  awarded: { label: 'Awarded', badge: 'bg-green-50 text-green-700' },
};

const TYPE_CONFIG: Record<string, { label: string; color: string; Icon: typeof Package }> = {
  material: { label: 'Material', color: 'bg-purple-50 text-purple-700', Icon: Package },
  transport: { label: 'Transport', color: 'bg-orange-50 text-orange-700', Icon: Truck },
};

const SOURCE_CONFIG: Record<string, { label: string; color: string }> = {
  manual: { label: 'Manual', color: 'bg-gray-100 text-gray-600' },
  email: { label: 'Email', color: 'bg-blue-50 text-blue-700' },
  phone: { label: 'Phone', color: 'bg-green-50 text-green-700' },
  portal: { label: 'Portal', color: 'bg-purple-50 text-purple-700' },
  whatsapp: { label: 'WhatsApp', color: 'bg-emerald-50 text-emerald-700' },
};

export function QuotationJobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [job, setJob] = useState<QuotationJob | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add Quotation form
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [awarding, setAwarding] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const [vendors, setVendors] = useState<VendorOption[]>([]);
  const [showMore, setShowMore] = useState(false);

  useEffect(() => {
    if (!showAddForm) return;
    // /vendors refuses limit > 100: page through (a plain limit=500 left this list empty).
    const ctl = new AbortController();
    loadVendorOptions(ctl.signal).then(setVendors).catch(() => { if (!ctl.signal.aborted) setVendors([]); });
    return () => ctl.abort();
  }, [showAddForm]);

  const [form, setForm] = useState({
    vendorId: '',
    vendorName: '',
    vendorContact: '',
    vendorPhone: '',
    rate: '',
    quantity: '',
    deliveryDays: '',
    notes: '',
    source: 'manual',
  });

  const fetchJob = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const res = await api.get(`/quotation-jobs/${id}`);
      setJob(res.data.data?.quotationJob ?? res.data.data ?? null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load quotation job');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchJob();
  }, [fetchJob]);

  // Auto-dismiss toast
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const handleAddQuotation = async () => {
    if (!id) return;
    try {
      setSaving(true);
      await api.post(`/quotation-jobs/${id}/quotations`, {
        ...(form.vendorId ? { vendorId: form.vendorId } : { vendorName: form.vendorName }),
        rate: parseFloat(form.rate) || 0,
        deliveryDays: form.deliveryDays === '' ? undefined : parseInt(form.deliveryDays, 10),
        source: form.source,
        ...(form.vendorContact ? { vendorContact: form.vendorContact } : {}),
        ...(form.vendorPhone ? { vendorPhone: form.vendorPhone } : {}),
        ...(form.quantity ? { quantity: parseFloat(form.quantity) } : {}),
        ...(form.notes ? { notes: form.notes } : {}),
      });
      setShowAddForm(false);
      setShowMore(false);
      setForm({ vendorId: '', vendorName: '', vendorContact: '', vendorPhone: '', rate: '', quantity: '', deliveryDays: '', notes: '', source: 'manual' });
      setToast({ message: 'Quotation added successfully', type: 'success' });
      fetchJob();
    } catch (err: any) {
      setToast({ message: err.response?.data?.error || 'Failed to add quotation', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleAward = async (quotationId: string) => {
    if (!id) return;
    if (!window.confirm('Award this quotation? This will create a Purchase Order automatically.')) return;
    try {
      setAwarding(quotationId);
      await api.patch(`/quotation-jobs/${id}/award/${quotationId}`);
      setToast({ message: 'Quotation awarded and Purchase Order created successfully', type: 'success' });
      fetchJob();
    } catch (err: any) {
      setToast({ message: err.response?.data?.error || 'Failed to award quotation', type: 'error' });
    } finally {
      setAwarding(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
        <span className="ml-2 text-sm" style={{ color: 'var(--text-secondary)' }}>Loading quotation job...</span>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <AlertTriangle size={40} className="mb-2 text-red-400" />
        <p className="text-sm text-red-500">{error || 'Quotation job not found'}</p>
        <button onClick={() => navigate('/quotation-jobs')} className="mt-3 text-sm text-[var(--ink-0)] hover:underline">
          Back to Quotation Jobs
        </button>
      </div>
    );
  }

  const statusConf = STATUS_CONFIG[job.status] || STATUS_CONFIG.open;
  const typeConf = TYPE_CONFIG[job.type] || TYPE_CONFIG.material;
  const TypeIcon = typeConf.Icon;
  const quotations = job.quotations || [];
  const hasAwarded = quotations.some((q) => q.isAwarded);

  return (
    <div>
      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 flex items-center gap-2 rounded-lg px-4 py-3 text-sm font-medium shadow-lg transition-all ${
            toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
          }`}
        >
          {toast.type === 'success' ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/quotation-jobs')}
            className="rounded-lg border border-gray-300 p-2 hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft size={18} style={{ color: 'var(--text-secondary)' }} />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
                {job.number}
              </h1>
              <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${statusConf.badge}`}>
                {statusConf.label}
              </span>
              <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${typeConf.color}`}>
                <TypeIcon size={12} />
                {typeConf.label}
              </span>
            </div>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Created {formatDate(job.createdAt)}
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          disabled={hasAwarded}
          className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus size={16} />
          Add Quotation
        </button>
      </div>

      {/* Info Card */}
      <div className="mb-6 rounded-xl border border-gray-200 p-5" style={{ backgroundColor: 'var(--bg-surface)' }}>
        <h2 className="mb-4 text-base font-semibold" style={{ color: 'var(--text-primary)' }}>Job Details</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <p className="mb-1 text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Description</p>
            <p className="text-sm" style={{ color: 'var(--text-primary)' }}>{job.description}</p>
          </div>
          <div>
            <p className="mb-1 text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Quantity</p>
            <div className="flex items-center gap-1 text-sm" style={{ color: 'var(--text-primary)' }}>
              <Hash size={14} />
              {job.quantity}{job.unit ? ` ${job.unit}` : ''}
            </div>
          </div>
          <div>
            <p className="mb-1 text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Target Rate</p>
            <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
              {job.targetRate != null ? formatINR(job.targetRate) : '-'}
            </p>
          </div>
          <div>
            <p className="mb-1 text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Required By</p>
            <div className="flex items-center gap-1 text-sm" style={{ color: 'var(--text-primary)' }}>
              <Calendar size={14} />
              {job.requiredByDate ? formatDate(job.requiredByDate) : '-'}
            </div>
          </div>
          <div>
            <p className="mb-1 text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Sales Order</p>
            <div className="flex items-center gap-1 text-sm">
              <FileText size={14} style={{ color: 'var(--text-secondary)' }} />
              {job.salesOrder ? (
                <button
                  onClick={() => navigate(`/sales-orders/${job.salesOrder.id}`)}
                  className="text-[var(--ink-0)] hover:underline"
                >
                  {job.salesOrder.number}
                </button>
              ) : (
                <span style={{ color: 'var(--text-secondary)' }}>-</span>
              )}
            </div>
          </div>
          <div>
            <p className="mb-1 text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Assigned To</p>
            <div className="flex items-center gap-1 text-sm" style={{ color: 'var(--text-primary)' }}>
              <User size={14} />
              {job.assignedTo || '-'}
            </div>
          </div>
        </div>
      </div>

      {/* Quotation Entries Table */}
      <div className="rounded-xl border border-gray-200 overflow-hidden" style={{ backgroundColor: 'var(--bg-surface)' }}>
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <h2 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>
            Quotation Entries ({quotations.length})
          </h2>
        </div>

        {quotations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <ClipboardList size={40} className="mb-2" />
            <p className="text-sm">No quotations yet</p>
            <p className="text-xs">Add vendor quotations to compare rates</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
                  <th className="px-4 py-3">Vendor</th>
                  <th className="px-4 py-3 text-right">Rate</th>
                  <th className="px-4 py-3 text-right">Quantity</th>
                  <th className="px-4 py-3 text-right">Total</th>
                  <th className="px-4 py-3 text-center">Delivery</th>
                  <th className="px-4 py-3 text-right">Margin</th>
                  <th className="px-4 py-3 text-center">Source</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {quotations.map((q) => {
                  const marginPositive = q.marginAmount != null && q.marginAmount >= 0;
                  const marginColor = q.marginAmount != null
                    ? (marginPositive ? 'text-green-600' : 'text-red-600')
                    : '';
                  const sourceConf = SOURCE_CONFIG[q.source] || SOURCE_CONFIG.manual;

                  return (
                    <tr
                      key={q.id}
                      className={`transition-colors ${q.isAwarded ? 'bg-green-50/50' : 'hover:bg-gray-50'}`}
                    >
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium" style={{ color: 'var(--text-primary)' }}>{q.vendorName}</p>
                          {q.vendorContact && (
                            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{q.vendorContact}</p>
                          )}
                          {q.vendorPhone && (
                            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{q.vendorPhone}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-medium" style={{ color: 'var(--text-primary)' }}>
                        {formatINR(q.rate)}
                      </td>
                      <td className="px-4 py-3 text-right" style={{ color: 'var(--text-secondary)' }}>
                        {q.quantity != null ? q.quantity : '-'}
                      </td>
                      <td className="px-4 py-3 text-right font-medium" style={{ color: 'var(--text-primary)' }}>
                        {q.totalAmount != null ? formatINR(q.totalAmount) : '-'}
                      </td>
                      <td className="px-4 py-3 text-center" style={{ color: 'var(--text-secondary)' }}>
                        {q.deliveryDays != null ? `${q.deliveryDays} day${q.deliveryDays !== 1 ? 's' : ''}` : '-'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {q.marginAmount != null ? (
                          <div className={`flex items-center justify-end gap-1 font-medium ${marginColor}`}>
                            {marginPositive ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                            <span>{formatINR(Math.abs(q.marginAmount))}</span>
                            {q.marginPercent != null && (
                              <span className="text-xs">({q.marginPercent.toFixed(1)}%)</span>
                            )}
                          </div>
                        ) : (
                          <span style={{ color: 'var(--text-secondary)' }}>-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${sourceConf.color}`}>
                          {sourceConf.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {q.isAwarded ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-700">
                            <Award size={12} />
                            Awarded
                          </span>
                        ) : (
                          <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {!q.isAwarded && !hasAwarded && (
                          <button
                            onClick={() => handleAward(q.id)}
                            disabled={awarding === q.id}
                            className="inline-flex items-center gap-1 rounded-lg border border-green-300 bg-green-50 px-3 py-1.5 text-xs font-medium text-green-700 hover:bg-green-100 transition-colors disabled:opacity-50"
                          >
                            {awarding === q.id ? (
                              <Loader2 size={12} className="animate-spin" />
                            ) : (
                              <Award size={12} />
                            )}
                            Award
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Notes section for quotations that have notes */}
      {quotations.some((q) => q.notes) && (
        <div className="mt-6 rounded-xl border border-gray-200 p-5" style={{ backgroundColor: 'var(--bg-surface)' }}>
          <h2 className="mb-3 text-base font-semibold" style={{ color: 'var(--text-primary)' }}>Quotation Notes</h2>
          <div className="space-y-3">
            {quotations
              .filter((q) => q.notes)
              .map((q) => (
                <div key={q.id} className="rounded-lg border border-gray-100 p-3">
                  <p className="mb-1 text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>
                    {q.vendorName} - {formatDate(q.createdAt)}
                  </p>
                  <p className="text-sm" style={{ color: 'var(--text-primary)' }}>{q.notes}</p>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Add Quotation Form Modal */}
      {showAddForm && (
        <Modal open onClose={() => setShowAddForm(false)}>
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 sticky top-0 z-10 rounded-t-xl" style={{ backgroundColor: 'var(--bg-surface)' }}>
              <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Add Quotation</h2>
              <RequestCloseButton className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </RequestCloseButton>
            </div>
            <div className="space-y-4 px-6 py-4">
              <div>
                <label htmlFor="q-supplier" className="mb-1 block text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Supplier *</label>
                <select id="q-supplier" value={form.vendorId} onChange={(e) => {
                    // SUN-285 A4: a supplier pick fills the contact person from the vendor record; a new supplier replaces it.
                    const picked = vendors.find((v) => v.id === e.target.value);
                    setForm({ ...form, vendorId: e.target.value, vendorContact: typeof picked?.contactPerson === 'string' ? picked.contactPerson.trim() : '' });
                  }}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none">
                  <option value="">Not in the list — type below</option>
                  {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
                {!form.vendorId && (
                  <input type="text" aria-label="Supplier name" value={form.vendorName} onChange={(e) => setForm({ ...form, vendorName: e.target.value })}
                    placeholder="Supplier name"
                    className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="q-rate" className="mb-1 block text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Rate (INR) *</label>
                  <input id="q-rate" type="number" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} placeholder="0"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                </div>
                <div>
                  <label htmlFor="q-days" className="mb-1 block text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Delivery days *</label>
                  <input id="q-days" type="number" min="0" value={form.deliveryDays} onChange={(e) => setForm({ ...form, deliveryDays: e.target.value })} placeholder="e.g. 7"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]" />
                </div>
              </div>
              <button type="button" onClick={() => setShowMore((s) => !s)} className="text-xs font-medium text-[var(--ink-0)] hover:underline">
                {showMore ? 'Fewer details' : 'More details'}
              </button>
              {showMore && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="mb-1 block text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Contact Person</label>
                      <input
                        type="text"
                        value={form.vendorContact}
                        onChange={(e) => setForm({ ...form, vendorContact: e.target.value })}
                        placeholder="Contact name"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Phone</label>
                      <input
                        type="text"
                        value={form.vendorPhone}
                        onChange={(e) => setForm({ ...form, vendorPhone: e.target.value })}
                        placeholder="+91 XXXXX XXXXX"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="mb-1 block text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Quantity</label>
                      <input
                        type="number"
                        value={form.quantity}
                        onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                        placeholder={job.quantity ? String(job.quantity) : '0'}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Source</label>
                      <select
                        value={form.source}
                        onChange={(e) => setForm({ ...form, source: e.target.value })}
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                      >
                        <option value="manual">Manual</option>
                        <option value="email">Email</option>
                        <option value="phone">Phone</option>
                        <option value="portal">Portal</option>
                        <option value="whatsapp">WhatsApp</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Notes</label>
                    <textarea
                      rows={2}
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      placeholder="Additional notes about this quotation..."
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                    />
                  </div>
                </>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <RequestCloseButton
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </RequestCloseButton>
              <button
                onClick={handleAddQuotation}
                disabled={saving || (!form.vendorId && !form.vendorName) || !form.rate || form.deliveryDays === ''}
                className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
              >
                {saving && <Loader2 size={14} className="animate-spin" />}
                Add Quotation
              </button>
            </div>
        </Modal>
      )}
    </div>
  );
}

function RequestCloseButton({ children, ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
  const { requestClose } = useModalClose();
  return <button type="button" {...props} onClick={requestClose}>{children}</button>;
}
